import { ReactNode, useContext } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../contexts/AuthContext";
import { ConnectWalletContext } from "../../contexts/ConnectWalletContext";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { ConnectWalletEmptyState, needsWalletConnect } from "./helpers";

/**
 * The desktop bottom bar's "connect your wallet" gate, for the mobile tabs.
 *
 * The mobile positions / orders / trade-history tabs rendered straight away and
 * answered a visitor with no wallet "You have no positions" — an empty account
 * is a claim, and there was no account. Children are not mounted at all until
 * there is one, so their own empty and error states only ever describe a real
 * account.
 */
export function MobileWalletGate({
  children,
  showDerivativeUi = true,
  gateOnboarding = true,
}: {
  children: ReactNode;
  showDerivativeUi?: boolean;
  /**
   * Also hold the gate while the account is "onboarding" (no funds). Right for
   * the trading page's bottom bar, which mirrors desktop. NOT for the portfolio
   * pages: a trader who withdrew everything is "onboarding" again and must still
   * see their orders, fills and funding history (desktop portfolio shows them).
   */
  gateOnboarding?: boolean;
}) {
  const { t } = useTranslation("app", {
    keyPrefix: "TradingBottomBar.TradingBottomBar",
  });
  const { account, accountApiKeyState } = useContext(AuthContext);
  const { setShowConnectModal } = useContext(ConnectWalletContext);
  const { showOnboarding } = useGetAccount();

  if (
    needsWalletConnect(
      account,
      accountApiKeyState,
      gateOnboarding ? showOnboarding : false
    )
  ) {
    return (
      <ConnectWalletEmptyState
        accountApiKeyState={accountApiKeyState}
        showDerivativeUi={showDerivativeUi}
        onConnect={() => setShowConnectModal(true)}
        t={t}
      />
    );
  }
  return <>{children}</>;
}
